import { useEffect, useMemo, useRef, useState } from 'react';
import { Editor } from 'primereact/editor';
import { useLanguage } from '../../contexts/LanguageContext';
import { isEmptyHtmlContent, isHtmlContent } from '../../utils/richTextUtils';

/**
 * Zengin metin bileşeni. Düzenleme ve gösterim aynı giriş noktasından sunulur;
 * başka formlarda da `value` / `onChange` / `display` ile kullanılabilir.
 *
 * Üretilen değer HTML'dir. Sunucuya gönderilen HTML temizlenmeli (sanitize).
 * `display` modu `dangerouslySetInnerHTML` kullanır; yalnızca sunucuda
 * temizlenmiş içerik verilmelidir.
 *
 * ÖNEMLİ: Varsayılan dışında bir biçimlendirme açılacaksa sunucudaki temizleme
 * allowlist'inin de o biçimlendirmeye izin vermesi gerekir; aksi halde kullanıcının
 * uyguladığı biçim kaydedilirken sessizce kaybolur.
 *
 * `readOnly` araç çubuğunu gösterir ama yazmayı kilitler (form kilidi).
 * `display` araç çubuğu olmadan kaydedilmiş içeriği basar (sohbet, özet).
 *
 * @example
 * // Düzenleme: kalın, italik, altı çizili, listeler, bağlantı
 * <RichTextEditor value={html} onChange={setHtml} maxLength={2500} onValidityChange={setValidity} />
 *
 * @example
 * // Kaydedilmiş içeriği gösterme
 * <RichTextEditor value={html} display />
 *
 * @example
 * // Renk ve başlık eklenmiş düzenleme
 * <RichTextEditor value={html} onChange={setHtml} formats={[...DEFAULT_EDITOR_FORMATS, 'color', 'header']} />
 */

/** Varsayılan biçimlendirmeler. Renk ve yazı boyutu bilinçli olarak dışarıda. */
export const DEFAULT_EDITOR_FORMATS = ['bold', 'italic', 'underline', 'list', 'link'];

/** Bileşenin desteklediği tüm biçimlendirmeler. */
export const ALL_EDITOR_FORMATS = ['header', 'font', 'size', 'bold', 'italic', 'underline', 'strike', 'color', 'background', 'script', 'list', 'indent', 'align', 'blockquote', 'code-block', 'link', 'clean'];

/**
 * Araç çubuğu düzeni. Her grup ayrı bir `.ql-formats` bloğu olur; Quill grupları
 * arasına boşluk koyar. Sıra bu dizideki sıradır.
 */
const TOOLBAR_GROUPS = [
    ['header', 'font', 'size'],
    ['bold', 'italic', 'underline', 'strike'],
    ['color', 'background'],
    ['script'],
    ['list', 'indent', 'align'],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean']
];

const RichTextDisplay = ({ value, className = '' }) => {
    if (!value) return null;

    if (isHtmlContent(value)) {
        return <div className={`rich-text-content ${className}`.trim()} dangerouslySetInnerHTML={{ __html: value }} />;
    }

    return <p className={`rich-text-plain ${className}`.trim()}>{value}</p>;
};

const RichTextEditorField = ({ id = 'richTextEditor', value, onChange, maxLength, onValidityChange, placeholder, label, formats = DEFAULT_EDITOR_FORMATS, readOnly = false, minHeight, className = '', style }) => {
    const { th } = useLanguage();

    // Düz metin uzunluğu yalnızca editörün kendi çıktısından bilinebildiği için
    // burada tutulur; içeriğin kendisi (HTML) çağıran tarafta kalır.
    const [plainText, setPlainText] = useState('');

    // İçerik dışarıdan temizlendiğinde (kayıt sonrası) sayaç da sıfırlanmalı.
    useEffect(() => {
        if (isEmptyHtmlContent(value)) {
            setPlainText('');
        }
    }, [value]);

    const plainLength = plainText.length;
    const isEmpty = isEmptyHtmlContent(value);
    const isOverLimit = typeof maxLength === 'number' && plainLength > maxLength;
    const canSubmit = !isEmpty && !isOverLimit;

    // Aynı değerlerle tekrar bildirim yapılmaz; aksi halde çağıran taraf state
    // güncelledikçe sonsuz döngü oluşur.
    const lastValidityRef = useRef('');
    useEffect(() => {
        const signature = `${plainLength}|${isEmpty}|${isOverLimit}|${canSubmit}`;
        if (lastValidityRef.current === signature) return;

        lastValidityRef.current = signature;
        onValidityChange?.({ plainLength, isEmpty, isOverLimit, canSubmit });
    }, [plainLength, isEmpty, isOverLimit, canSubmit, onValidityChange]);

    // Bağlantı balonu metinleri Quill'in paket CSS'inde İngilizce sabittir; çeviriler
    // CSS değişkeni olarak geçirilir ve _editor.scss bunları `content` ile kullanır.
    const textVariables = useMemo(
        () => ({
            '--rte-enter-link': JSON.stringify(th('common.editor.enterLink')),
            '--rte-visit-url': JSON.stringify(th('common.editor.visitUrl')),
            '--rte-save': JSON.stringify(th('common.editor.save')),
            '--rte-edit': JSON.stringify(th('common.editor.edit')),
            '--rte-remove': JSON.stringify(th('common.editor.remove')),
            ...(minHeight ? { '--rte-min-height': minHeight } : {})
        }),
        [th, minHeight]
    );

    const header = useMemo(() => {
        // title görünür ipucunu, aria-label ekran okuyucu adını sağlar.
        const text = (key) => th(`common.editor.${key}`);
        const button = (format, key, extra = {}) => <button key={key} type="button" className={`ql-${format}`} title={text(key)} aria-label={text(key)} {...extra} />;

        // Renk seçicilerinde seçenekleri Quill kendisi doldurur.
        const picker = (format, key, options) => (
            <select key={key} className={`ql-${format}`} title={text(key)} aria-label={text(key)}>
                {options?.map((option) => (
                    <option key={option.value ?? 'default'} value={option.value}>
                        {option.label ? text(option.label) : undefined}
                    </option>
                ))}
            </select>
        );

        const controls = {
            header: () =>
                picker('header', 'heading', [
                    { value: '1', label: 'heading1' },
                    { value: '2', label: 'heading2' },
                    { value: '3', label: 'heading3' },
                    { value: '0', label: 'normalText' }
                ]),
            font: () => picker('font', 'font', [{ value: '' }, { value: 'serif' }, { value: 'monospace' }]),
            size: () => picker('size', 'fontSize', [{ value: 'small' }, { value: '' }, { value: 'large' }, { value: 'huge' }]),
            bold: () => button('bold', 'bold'),
            italic: () => button('italic', 'italic'),
            underline: () => button('underline', 'underline'),
            strike: () => button('strike', 'strikethrough'),
            color: () => picker('color', 'textColor'),
            background: () => picker('background', 'backgroundColor'),
            script: () => [button('script', 'subscript', { value: 'sub' }), button('script', 'superscript', { value: 'super' })],
            list: () => [button('list', 'bulletList', { value: 'bullet' }), button('list', 'orderedList', { value: 'ordered' })],
            indent: () => [button('indent', 'outdent', { value: '-1' }), button('indent', 'indent', { value: '+1' })],
            align: () => picker('align', 'align', [{ value: '' }, { value: 'center' }, { value: 'right' }, { value: 'justify' }]),
            blockquote: () => button('blockquote', 'blockquote'),
            'code-block': () => button('code-block', 'codeBlock'),
            link: () => button('link', 'link'),
            clean: () => button('clean', 'clearFormatting')
        };

        return TOOLBAR_GROUPS.map((group) => {
            const active = group.filter((format) => formats.includes(format));
            if (active.length === 0) return null;

            return (
                <span key={group.join('-')} className="ql-formats">
                    {active.map((format) => controls[format]())}
                </span>
            );
        }).filter(Boolean);
    }, [formats, th]);

    const handleTextChange = (event) => {
        onChange?.(event.htmlValue ?? '');
        // Editör çıktısının sonuna satır sonu ekler; sayaçta görünmemeli.
        setPlainText((event.textValue ?? '').replace(/\n+$/, ''));
    };

    const counterId = `${id}Counter`;
    const showCounter = typeof maxLength === 'number';

    return (
        // Değişkenler sarmalayıcıya verilir: Editor'ün style prop'u içerik alanına
        // uygulanır ve bağlantı balonu o alanın dışında kalır.
        <div style={{ ...textVariables, ...style }}>
            <label htmlFor={id} className="sr-only">
                {label ?? th('common.editor.label')}
            </label>

            <Editor
                id={id}
                className={`rich-text-editor ${className}`.trim()}
                value={value}
                onTextChange={handleTextChange}
                headerTemplate={header}
                formats={formats}
                placeholder={placeholder ?? th('common.editor.placeholder')}
                readOnly={readOnly}
                aria-describedby={showCounter ? counterId : undefined}
            />

            {/* Sayaç düz metin üzerinden gösterilir; kullanıcı etiket uzunluğuyla
                ilgilenmez. Aşım renk dışında ikon ve kalınlıkla da belirtilir. */}
            {showCounter && (
                <small id={counterId} className={`block text-right mt-1 ${isOverLimit ? 'p-error font-semibold' : 'text-500'}`} aria-live="polite">
                    {isOverLimit && <i className="pi pi-exclamation-triangle mr-1" aria-hidden="true" />}
                    {plainLength} / {maxLength}
                </small>
            )}
        </div>
    );
};

const RichTextEditor = ({ display = false, ...props }) => {
    if (display) {
        return <RichTextDisplay value={props.value} className={props.className} />;
    }

    return <RichTextEditorField {...props} />;
};

export default RichTextEditor;
